/**
 * Pure field-check builder for Add/Edit Company (SHARED-FORM-SHELL-004, LLD §3.6).
 *
 * This moves the body of FirmForm's old `validateFieldErrors` verbatim, emitting one entry
 * per field it EXAMINES — whether or not that field fails — so progress (FormShell) and
 * validation (`validateFirmFields`) read the same list and cannot disagree (HLD §4).
 *
 * `filled` is derived from the trimmed value, never from `!error`: an invalid-but-filled
 * email must still count as filled for progress (LLD §3.6 trap).
 */
import {
  isValidEmail,
  mustValidateRow,
  isStartedContact,
  isStartedAddress,
  isStartedBankAccount,
} from "./professionalContacts.rules";

export const FIRM_SECTIONS = [
  "details",
  "contacts",
  "addresses",
  "bank",
  "pii",
  "notes",
];

const trimmed = (value) => (value ?? "").toString().trim();

/**
 * @param {import("./professionalContacts.types").FirmUpsert} draft
 * @returns {Array<{ fieldId: string, sectionId: string, label: string, filled: boolean, error: string|null }>}
 */
export function buildFirmFieldChecks(draft) {
  const checks = [];
  const push = (fieldId, sectionId, label, filled, error = null) => {
    checks.push({ fieldId, sectionId, label, filled, error });
  };

  // Company details — always examined.
  push("firm-type", "details", "Type", Boolean(draft?.type), draft?.type ? null : "Type is required");
  push(
    "firm-name",
    "details",
    "Company name",
    Boolean(trimmed(draft?.name)),
    trimmed(draft?.name) ? null : "Company name is required"
  );
  push(
    "firm-mainPhone",
    "details",
    "Main contact number",
    Boolean(trimmed(draft?.mainPhone)),
    trimmed(draft?.mainPhone) ? null : "Main contact number is required"
  );
  {
    const email = trimmed(draft?.email);
    const error = !email
      ? "Company email address is required"
      : !isValidEmail(draft.email)
      ? "Please enter a valid email address"
      : null;
    push("firm-email", "details", "Company email address", Boolean(email), error);
  }

  const contacts = draft?.contacts || [];
  const hasCompleteContact = contacts.some(
    (c) =>
      c.name?.trim() && c.email?.trim() && isValidEmail(c.email) && c.phone?.trim()
  );

  contacts.forEach((contact, index) => {
    if (mustValidateRow(contact, isStartedContact) || (!hasCompleteContact && index === 0)) {
      const row = `Individual ${index + 1}`;
      push(
        `contact-name-${contact._key}`,
        "contacts",
        `${row} · Name`,
        Boolean(contact.name?.trim()),
        contact.name?.trim() ? null : "Name is required"
      );
      {
        const email = contact.email?.trim();
        const error = !email
          ? "Email is required"
          : !isValidEmail(contact.email)
          ? "Please enter a valid email address"
          : null;
        push(`contact-email-${contact._key}`, "contacts", `${row} · Email`, Boolean(email), error);
      }
      push(
        `contact-phone-${contact._key}`,
        "contacts",
        `${row} · Phone`,
        Boolean(contact.phone?.trim()),
        contact.phone?.trim() ? null : "Phone is required"
      );
    }
  });

  const addresses = draft?.addresses || [];
  const hasCompleteAddress = addresses.some(
    (a) =>
      a.building?.trim() &&
      a.street?.trim() &&
      a.town?.trim() &&
      a.postcode?.trim() &&
      a.country?.trim()
  );

  addresses.forEach((address, index) => {
    if (mustValidateRow(address, isStartedAddress) || (!hasCompleteAddress && index === 0)) {
      const row = `Address ${index + 1}`;
      push(
        `address-building-${address._key}`,
        "addresses",
        `${row} · Building`,
        Boolean(address.building?.trim()),
        address.building?.trim() ? null : "Building is required"
      );
      push(
        `address-street-${address._key}`,
        "addresses",
        `${row} · Street`,
        Boolean(address.street?.trim()),
        address.street?.trim() ? null : "Street is required"
      );
      push(
        `address-town-${address._key}`,
        "addresses",
        `${row} · Town`,
        Boolean(address.town?.trim()),
        address.town?.trim() ? null : "Town is required"
      );
      push(
        `address-postcode-${address._key}`,
        "addresses",
        `${row} · Postcode`,
        Boolean(address.postcode?.trim()),
        address.postcode?.trim() ? null : "Postcode is required"
      );
      push(
        `address-country-${address._key}`,
        "addresses",
        `${row} · Country`,
        Boolean(address.country?.trim()),
        address.country?.trim() ? null : "Country is required"
      );
    }
  });

  const bankAccounts = draft?.bankAccounts || [];
  bankAccounts.forEach((bankAccount, index) => {
    if (mustValidateRow(bankAccount, isStartedBankAccount)) {
      const row = `Bank account ${index + 1}`;
      // Field order matches the old recordError order, so firstInvalidField lands the same place.
      push(
        `bank-beneficiaryName-${bankAccount._key}`,
        "bank",
        `${row} · Beneficiary name`,
        Boolean(bankAccount.beneficiaryName?.trim()),
        bankAccount.beneficiaryName?.trim() ? null : "Beneficiary name is required"
      );
      push(
        `bank-bankType-${bankAccount._key}`,
        "bank",
        `${row} · Account type`,
        Boolean(trimmed(bankAccount.bankType)),
        trimmed(bankAccount.bankType) ? null : "Account type is required"
      );
      push(
        `bank-accountNumber-${bankAccount._key}`,
        "bank",
        `${row} · Account number`,
        Boolean(bankAccount.accountNumber?.trim()),
        bankAccount.accountNumber?.trim() ? null : "Account number is required"
      );
      push(
        `bank-sortCode-${bankAccount._key}`,
        "bank",
        `${row} · Sort code`,
        Boolean(bankAccount.sortCode?.trim()),
        bankAccount.sortCode?.trim() ? null : "Sort code is required"
      );
      push(
        `bank-bankName-${bankAccount._key}`,
        "bank",
        `${row} · Creditor bank name`,
        Boolean(bankAccount.bankName?.trim()),
        bankAccount.bankName?.trim() ? null : "Creditor bank name is required"
      );
      push(
        `bank-town-${bankAccount._key}`,
        "bank",
        `${row} · Creditor bank town`,
        Boolean(bankAccount.town?.trim()),
        bankAccount.town?.trim() ? null : "Creditor bank town is required"
      );
      push(
        `bank-postcode-${bankAccount._key}`,
        "bank",
        `${row} · Creditor bank postcode`,
        Boolean(bankAccount.postcode?.trim()),
        bankAccount.postcode?.trim() ? null : "Creditor bank postcode is required"
      );
      push(
        `bank-country-${bankAccount._key}`,
        "bank",
        `${row} · Creditor bank country`,
        Boolean(bankAccount.country?.trim()),
        bankAccount.country?.trim() ? null : "Creditor bank country is required"
      );
      push(
        `bank-address-${bankAccount._key}`,
        "bank",
        `${row} · Creditor bank address`,
        Boolean(bankAccount.address?.trim()),
        bankAccount.address?.trim() ? null : "Creditor bank address is required"
      );
    }
  });

  return checks;
}

/**
 * `validateFieldErrors`'s old return shape, derived from the checks: only entries with an
 * error contribute, in check order, so `firstInvalidField` lands on the same field as before.
 * @param {import("./professionalContacts.types").FirmUpsert} draft
 * @returns {{ errors: Record<string, string>, firstInvalidField: string|null }}
 */
export function validateFirmFields(draft) {
  const checks = buildFirmFieldChecks(draft);
  const errors = {};
  let firstInvalidField = null;
  checks.forEach((check) => {
    if (check.error) {
      errors[check.fieldId] = check.error;
      if (!firstInvalidField) firstInvalidField = check.fieldId;
    }
  });
  return { errors, firstInvalidField };
}
